import React from "react";
import BrandHeader from "./components/BrandHeader";
import { createEntryPage } from "../FullPageLayout/FullPageLayout";
import "cross-fetch/polyfill";
import Box from "../../elements/v2/Box/Box";
import SearchHeader from "./components/SearchHeader";
import { SearchPageProvider } from "./SearchPageContext";
import { Route, Switch } from "react-router-dom";
import ProfileReviewsPage from "./components/ProfilePage/ProfileReviewsPage";
import CollectionsBlock from "./components/ProfilePage/components/CollectionsBlock/CollectionsBlock";
import ProfileContactPage from "./components/ProfilePage/ProfileContactPage";
import styled from "styled-components";
import { SellerType } from "../../../types/graphql-global-types";
import FlexBox from "../../elements/v2/Box/FlexBox";
import SmartImage from "../../composites/SmartImage/SmartImage";
import InfoBox from "./components/ProfilePage/components/InfoBox/InfoBox";
import ListingsEngagementBlock from "./components/ProfilePage/components/ListingsEngagementBlock/ListingsEngagementBlock";
import { breakpoints } from "../../../utils/styledHelpers";
import SearchContent from "./components/SearchContent";
import { FilterProps } from "../../../utils/algolia/filters";
import { GetSearchProps } from "./queries/types/GetSearchProps";

function optionalizeLocale(path) {
  return path.replace(/^\/en\//, "*/");
}

export type SearchPageProps = FilterProps & {
  remoteIp?: string;
  featuredBrandLogoUrl?: string;
  featuredBrandHeaderUrl?: string;
  searchProps?: GetSearchProps;
};

function SearchPage(pageProps: SearchPageProps) {
  const {
    featuredBrandLogoUrl,
    featuredBrandHeaderUrl,
    brand,
    profilePage,
    conciergePage,
  } = pageProps;
  const isProfessionalSeller =
    profilePage && profilePage.person.sellerType === SellerType.professional_seller;
  const showHeaderImage = isProfessionalSeller && profilePage.person.coverImageUrl;
  const showEngagements = profilePage && (profilePage.person.isMe || profilePage.me?.isAdmin);

  return (
    <SearchPageProvider searchPageProps={pageProps}>
      <Wrapper>
        {showHeaderImage && (
          <Header>
            <BackgroundImage imageUrl={profilePage.person.coverImageUrl} />
          </Header>
        )}
        {(profilePage || conciergePage) && (
          <Section>
            <InfoBox profilePage={profilePage} conciergePage={conciergePage} />
            {showEngagements && <ListingsEngagementBlock />}
          </Section>
        )}
        <Switch>
          {profilePage && (
            <Route exact path={optionalizeLocale(profilePage.person.reviewsUrl)}>
              <Section>
                <ProfileReviewsPage />
              </Section>
            </Route>
          )}
          {profilePage && (
            <Route exact path={optionalizeLocale(profilePage.person.collectionsUrl)}>
              <Section>
                <CollectionsBlock pageParam="c" />
              </Section>
            </Route>
          )}
          {profilePage && (
            <Route exact path={optionalizeLocale(profilePage.person.contactUrl)}>
              <Section>
                <ProfileContactPage />
              </Section>
            </Route>
          )}
          <Route path="*">
            <Box pt={3} px={[0, 2]}>
              {brand && (
                <BrandHeader
                  brand={brand}
                  featuredBrandHeaderUrl={featuredBrandHeaderUrl}
                  featuredBrandLogoUrl={featuredBrandLogoUrl}
                />
              )}
              <SearchHeader />
              <SearchContent />
            </Box>
          </Route>
        </Switch>
      </Wrapper>
    </SearchPageProvider>
  );
}

const Wrapper = styled.div`
  width: 100%;
  padding-bottom: 24px;
`;

const Header = styled(FlexBox)`
  width: 100%;
  max-height: 196px;
  overflow: hidden;
  background: #f8eee7;
  padding: 0;
  margin: 0;
  align-items: center;
`;

const BackgroundImage = styled(SmartImage)`
  width: 100%;
`;

const Section = styled(Box)`
  width: 100%;
  padding: 24px;
  ${breakpoints.up("sm")} {
    padding-left: 40px;
    padding-right: 40px;
  }}
`;

export interface Args {
  topbarProps: any;
  componentProps: SearchPageProps;
}

const mapEntryProps = (args: Args) => {
  return {
    pageProps: args,
    componentProps: args.componentProps,
    algolia: true,
  };
};

export default createEntryPage(mapEntryProps)(SearchPage);
